import api from '@/shared/utils/api';

/**
 * Radar 360 (F2) — typed client for the BFF `/api/v1/radar` surface.
 *
 * All calls go through the shared `apiClient` (→ `/api/v1`, BFF). The frontend
 * NEVER talks to a backend service directly (ver CLAUDE.md "REGRA DE
 * ARQUITETURA — FRONTEND SEMPRE PASSA PELO BFF").
 */

const BASE = '/radar';

/** Status lifecycle of a single recommendation (spec §5). */
export type RadarStatus =
  | 'novo'
  | 'lead_criado'
  | 'em_analise'
  | 'deal_criado'
  | 'dispensado';

/** Pool — whether the company already has decision-makers mapped. */
export type RadarPool = 'decisores' | 'sem_decisor';

/**
 * A single Radar recommendation (batched monthly, per the scheduler's
 * cadence — see janus-infra's `radar_scheduler.tf`). Deliberately a DISTINCT type from
 * `DiscoveredCompany` (prospects module) — the Radar carries lifecycle state
 * (status / leadId / dealId) and a normalized 0–10 fit score, not the raw
 * BigQuery row shape.
 */
/**
 * Um sinal fiscal empilhado numa recomendação (Radar v2).
 *
 * CONTRATO LGPD: só campos INSTITUCIONAIS (PJ). O scrub acontece no
 * janus-machine-ai e é re-assertado por teste no janus-bff — nunca adicionar
 * aqui campo derivado de pessoa (nome/CPF de sócio, e-mail, telefone).
 */
/** V103 — aviso de contexto na recomendação (selo discreto). */
export interface RadarAviso {
  tipo: string;
  texto: string;
}

export interface SinalEmpilhado {
  /** Ex.: 'saiu_simples', 'ganhou_contrato_publico', 'nova_filial', 'novo_socio'. */
  tipoSinal: string;
  /** Data do evento fiscal (ISO). */
  dataEvento?: string | null;
  /** Severidade após o decay temporal. */
  severidadeEfetiva?: number;
  /** Frase institucional do que aconteceu, montada no backend a partir da
   *  evidência JÁ filtrada — ex.: "Venceu contrato público de R$ 17.500 com
   *  Municipio De Planalto (fonte: PNCP)". Nunca deriva do `trigger_text` bruto. */
  descricao?: string;
  /** Evidência estruturada, filtrada por WHITELIST por tipo no janus-machine-ai.
   *  Só campos institucionais (PJ): valor/órgão de contrato, município/UF de
   *  filial, motivo de suspensão, datas. `novo_socio` traz APENAS a data —
   *  `nome_socio` é o único PII da base e nunca chega aqui. */
  evidencia?: SinalEvidencia;
}

/** Campos de evidência permitidos (união das whitelists por tipo de sinal).
 *  Tipado explicitamente — e NÃO como `Record<string, unknown>` — para que
 *  adicionar um campo aqui seja uma decisão consciente e revisável. */
export interface SinalEvidencia {
  // ⚠️ camelCase: o `q2cRequest` do BFF aplica `toCamelCase` RECURSIVAMENTE, então
  // as chaves DENTRO de `evidencia` também são convertidas (`orgao_cnpj` chega
  // como `orgaoCnpj`). Declarar em snake_case aqui renderizaria `undefined` em
  // silêncio.
  // ganhou_contrato_publico
  valor?: number;
  orgao?: string;
  orgaoCnpj?: string;
  contrato?: string;
  dataAssinatura?: string;
  fonte?: string;
  // nova_filial
  municipio?: string;
  uf?: string;
  dataInicio?: string;
  // saiu_simples / optou_..._inferido
  dataExclusaoSimples?: string;
  inferido?: boolean;
  baseInferencia?: string;
  porte?: string;
  cnae?: string;
  // novo_socio (APENAS a data — `nomeSocio` nunca chega aqui)
  dataEntrada?: string;
  // suspensao_cadastral / situacao_especial
  motivoDescricao?: string;
  motivoCodigo?: string;
  situacaoCadastral?: string;
  dataSituacao?: string;
  dataSituacaoEspecial?: string;
}

export interface RadarOfferOption {
  recorteId?: string | null;
  recorte_id?: string | null;
  recorteNome?: string | null;
  recorte_nome?: string | null;
  productId?: string | null;
  product_id?: string | null;
  productName?: string | null;
  product_name?: string | null;
  icpScore?: number | null;
  icp_score?: number | null;
  fitScore?: number;
  fit_score?: number;
  porQueAgora?: string | null;
  por_que_agora?: string | null;
  icpRationale?: string | null;
  icp_rationale?: string | null;
  justificativa?: string | null;
  abordagemHook?: string | null;
  abordagem_hook?: string | null;
  decisorAlvo?: string | null;
  decisor_alvo?: string | null;
  isPrimary?: boolean;
  is_primary?: boolean;
}

export interface RadarRecommendation {
  id: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  setor?: string;
  /** Primary CNAE (Brazilian economic-activity code) — secondary card info. */
  cnae?: string;
  /** Company size band (e.g. "Grande", "Média") — secondary card info. */
  porte?: string;
  /** Normalized fit score, 0–10 (determinístico — uso interno/seleção). */
  fitScore: number;
  /** Fit do ICP 0–100 (LLM) — escala OFICIAL exibida (igual ao painel "Fit com
   *  o ICP" e ao card de Lead). Ausente até o scoring LLM rodar. */
  icpScore?: number | null;
  /** True when decision-makers are already mapped (pool === 'decisores'). */
  hasDecisores: boolean;
  /** Nº de estabelecimentos ATIVOS da raiz no instante da recomendação.
   *
   *  `undefined` = **não medido nesta rodada** — nunca "zero unidades". O motor
   *  só o mede quando a Régua declara um modo de ordenação por unidades, e a
   *  coluna da base viva (`UNIDADES_ATIVAS`) nasce no promote mensal. Uma
   *  empresa com zero estabelecimentos ativos não existiria na base, então
   *  exibir "0" aqui seria afirmar um número que ninguém mediu: mostre "—". */
  unidadesAtivas?: number | null;
  pool: RadarPool;
  /** Trigger event that surfaced this company (pill). */
  triggerEvent?: string;
  /** Lazy-loaded approach hook (ver doc 04). Hoje NUNCA populado por nenhum
   *  serviço — mantido no contrato para quando esse hook existir; o
   *  fallback real de "por que esta empresa" hoje é `icpRationale`. */
  abordagemHook?: string;
  /** Justificativa em pt-BR gerada pela IA para o `icpScore` — a MESMA
   *  exibida no painel "Fit com o ICP". Ausente até o scoring LLM rodar.
   *  Achado 2026-08-21: para um tenant sem o signal-boost do WP-8 (sem
   *  `porQueAgora`), este é o único texto real disponível explicando a
   *  recomendação — sem ele o slot de "por que esta empresa" ficava em
   *  branco em toda a listagem. */
  icpRationale?: string;
  icpStrengths?: string[];
  icpWeaknesses?: string[];

  // ── Radar v2 — sinal fiscal (WP-8) ────────────────────────────────────────
  // Achatados pelo BFF a partir do `trigger_meta` do sales-api. Só presentes
  // para tenants com o signal-boost ligado (hoje: piloto 4670) — todos
  // opcionais, então um item sem eles renderiza exatamente como antes.
  /** Frase única "por que agora", determinística e SEM PII (derivada do tipo
   *  do sinal dominante, nunca do trigger_text bruto, que pode conter nome de
   *  sócio). Substitui o `abordagemHook` como destaque quando presente. */
  porQueAgora?: string;
  /** Sinais fiscais empilhados — apenas dados institucionais (PJ). */
  sinaisEmpilhados?: SinalEmpilhado[];
  /** Nº de TIPOS DISTINTOS de sinal (após dedup por tipo). */
  nSinais?: number;
  /** Calor do empilhamento (Σ severidade com decay × bônus de diversidade).
   *  ⚠️ ESCALA ABERTA — NÃO é 0–10 nem 0–100. Nunca formatar com as helpers de
   *  fit/ICP (`fitColor`/`fitLabel`): só faz sentido como ordenação relativa. */
  stackScore?: number;
  /** A empresa entrou APESAR de reprovada no limiar de ICP, resgatada pelo
   *  calor excepcional do sinal (WP-3). Presente só quando `true`. O vendedor
   *  precisa dessa pista: é a única diferença entre "o modelo acha que combina
   *  com você" e "o modelo acha que NÃO combina, mas esta empresa acabou de se
   *  mexer muito". */
  resgatadoPorSinal?: boolean;
  // ── V103 — contexto e medição ────────────────────────────────────────────
  /** Contexto factual (ex.: multa da ANS em cobrança). NUNCA entra no ranking
   *  nem no "por que agora" — só um selo discreto, sem acusar. */
  avisos?: RadarAviso[];
  /** Famílias de sinal que convergem nesta empresa (ex.: expansao_fisica). */
  familias?: string[];
  /** Momento comercial do sinal principal (Expansão, Regulatório, …). */
  momento?: string;
  /** A fonte do sinal principal está atrasada além do SLO. */
  defasado?: boolean;
  // ── V112 — playbook de setor (motor desligado para todos hoje) ──────────
  /** Código do playbook que gerou a rec (ex.: locacao_frotas.demanda). */
  playbookId?: string;
  /** Versão imutável do playbook usada no run. */
  playbookVersao?: number;
  /** Momento do playbook (ou ``plataforma.<familia>`` quando nenhum casou). */
  momentoCodigo?: string;
  /** Produto do tenant indicado para a abordagem (ex.: P3). */
  produtoCodigo?: string;
  /** Território do playbook em que a empresa caiu. */
  territorioCodigo?: string;
  /** Semana ISO do lote que gerou a recomendação. */
  batchWeek?: string;
  status: RadarStatus;
  /** Present once the recommendation became a lead. */
  leadId?: string;
  /** Present once the lead was promoted to a deal. */
  dealId?: string;
  /** ISO timestamp of when the recommendation was created. */
  recommendedAt: string;

  // ── Radar Multi-Oferta (Recorte de ICP por Produto) ───────────────────────
  recorteId?: string | null;
  recorte_id?: string | null;
  recorteNome?: string | null;
  recorte_nome?: string | null;
  productId?: string | null;
  product_id?: string | null;
  productName?: string | null;
  product_name?: string | null;
  isCrossSellOpportunity?: boolean;
  is_cross_sell_opportunity?: boolean;
  primaryOffer?: RadarOfferOption | null;
  primary_offer?: RadarOfferOption | null;
  alternativeOffers?: RadarOfferOption[];
  alternative_offers?: RadarOfferOption[];
  alternativeRecortes?: RadarOfferOption[];
  alternative_recortes?: RadarOfferOption[];
}

/** Tenant-scoped Radar configuration (spec §2). */
export interface RadarConfig {
  /** Whether the Radar is currently ON for this tenant. */
  radar_enabled: boolean;
  /** Whether "Gerar deal automaticamente" is ON: after each Radar run
   *  (monthly), every created lead is promoted to a deal and its ICP
   *  decisores are revealed. */
  radar_auto_deal: boolean;
  /** Whether the current user's role may flip the switch. */
  can_toggle: boolean;
  /** Plan slug — used to gate free plans into the upsell state. */
  plan: string;
  /** Monthly recommendation quota for the current plan. */
  quota: number;
}

/** Paginated recommendation list (spec §3 — `{items, total, ...}`). */
export interface RadarListResponse {
  items: RadarRecommendation[];
  total: number;
  limit: number;
  offset: number;
  /**
   * Quantas empresas de alto fit o teto do plano reteve (merchandising).
   *
   * **As DUAS grafias, e o camelCase é o que chega HOJE** — MEDIDO 2026-09-08:
   * o `q2cRequest` do janus-bff faz `toCamelCase` no envelope INTEIRO
   * (`salesServiceClient.ts:188`), então `withheld_count` chega como
   * `withheldCount`. O tipo antigo declarava só a grafia snake — escrito a
   * partir do CONTRATO do sales-api, não do PAYLOAD — e por isso concordava
   * com uma leitura que sempre devolvia `undefined`.
   *
   * Leia por `lib/envelopeDaLista.retidasPeloPlano`, nunca direto: a regra
   * (as duas grafias + `null` ≠ `0`) tem UM dono.
   */
  withheldCount?: number;
  /** @see withheldCount — a grafia do sales-api, se o hop virar `q2cRequestRaw`. */
  withheld_count?: number;
  /**
   * Rótulo do lote mais recente do TENANT.
   *
   * @see withheldCount para o porquê das duas grafias. Leia por
   * `lib/envelopeDaLista.loteMaisRecente`.
   */
  batchWeek?: string;
  /** @see batchWeek — a grafia do sales-api. */
  batch_week?: string;
}

/**
 * A single news item about a company (F3). Most fields are optional — news
 * providers vary in how much structured metadata they return.
 */
export interface NewsItem {
  title: string;
  summary: string;
  /** Publisher / outlet name (e.g. "Valor Econômico"). */
  source?: string;
  /** Canonical URL of the article. The BFF camelCases the backend `source_url`
   *  to `sourceUrl`; `source_url` kept as a legacy fallback. */
  sourceUrl?: string;
  source_url?: string;
  /** ISO date (or date-time) the article was published. */
  date?: string;
  /** Normalized relevance, 0–1 (or 0–10). BFF camelCases `relevance_score`. */
  relevanceScore?: number;
  relevance_score?: number;
}

/** Company news payload (F3 — `GET /radar/companies/{cnpj}/news`). */
export interface CompanyNews {
  cnpj: string;
  items: NewsItem[];
  /** ISO timestamp of when this batch was fetched/cached server-side.
   *  BFF camelCases `fetched_at` -> `fetchedAt`. */
  fetchedAt?: string;
  fetched_at?: string;
  /** True when the response was served from the server-side cache. */
  cached: boolean;
}

/** Query params accepted by `list()`. */
export interface RadarListParams {
  limit?: number;
  offset?: number;
  status?: RadarStatus;
  pool?: RadarPool;
  /** Fit mínimo na escala 0–10 do score DETERMINÍSTICO (seleção interna).
   *  ⚠️ NÃO é a escala que o card exibe — para essa, use `minIcp`. Mantido para
   *  quem já o usa; a tela migrou para `minIcp` em 2026-09-08, porque um
   *  controle "fit mínimo" que corta noutra escala é um filtro que filtra, mas
   *  não o que o operador viu. */
  min_fit?: number;
  /** Fit mínimo na escala 0–100 do ICP (LLM) — a MESMA exibida no card. */
  minIcp?: number;
  /** Busca em razão social, nome fantasia e CNPJ. */
  q?: string;
  /** `true` = só com gatilho fiscal; `false` = só sem. Ausente = os dois. */
  comGatilho?: boolean;
  /** Filtra por um tipo de sinal (ex.: `nova_filial`). */
  tipoSinal?: string;
  /** Só as recomendações deste lote (ISO date do `batch_week`). */
  batchWeek?: string;
  /** Ordenação da lista — vocabulário FECHADO, espelho de
   *  `radar_service.SORTS_DA_LISTA` no sales-api. Valor fora dele é 422. */
  sort?: string;
  /** Filtro por Recorte de ICP / Linha de Oferta (Multi-Oferta). */
  recorte_id?: string;
  recorteId?: string;
  product_id?: string;
  productId?: string;
  /** Filtra apenas oportunidades de Cross-Sell (empresas já clientes em outro produto). */
  is_cross_sell_opportunity?: boolean;
  isCrossSellOpportunity?: boolean;
}

export const radarApi = {
  /** GET /api/v1/radar/config — tenant config + plan/quota. */
  async getConfig(): Promise<RadarConfig> {
    const { data } = await api.get<RadarConfig>(`${BASE}/config`);
    return data;
  },

  /** PUT /api/v1/radar/config — toggle the Radar on/off. */
  async setConfig(enabled: boolean): Promise<RadarConfig> {
    const { data } = await api.put<RadarConfig>(`${BASE}/config`, {
      radar_enabled: enabled,
    });
    return data;
  },

  /** PUT /api/v1/radar/config — toggle "Gerar deal automaticamente" (independent
   *  of radar_enabled; the BFF merges only the flag sent). */
  async setAutoDeal(enabled: boolean): Promise<RadarConfig> {
    const { data } = await api.put<RadarConfig>(`${BASE}/config`, {
      radar_auto_deal: enabled,
    });
    return data;
  },

  /** GET /api/v1/radar/recommendations — paginated, filterable, date-desc. */
  async list(params: RadarListParams = {}): Promise<RadarListResponse> {
    const { data } = await api.get<RadarListResponse>(`${BASE}/recommendations`, {
      params,
    });
    return data;
  },

  /** POST /api/v1/radar/recommendations/{id}/dismiss — mark as dispensado. */
  async dismiss(id: string): Promise<RadarRecommendation> {
    const { data } = await api.post<RadarRecommendation>(
      `${BASE}/recommendations/${id}/dismiss`,
    );
    return data;
  },

  /**
   * POST /api/v1/radar/recommendations/{id}/viewed — telemetria de produto.
   *
   * Registra que o usuário ABRIU esta recomendação. Do outro lado, o sales-api
   * grava em `janus_recommendations.recommendation_events` — a tabela que
   * existia desde a Fase 0 do Radar com zero linhas, e cuja falta de escritor
   * é o motivo de ninguém saber se alguém usa o Radar.
   *
   * **Nunca levanta.** Telemetria que quebra a navegação é pior que telemetria
   * nenhuma: quem chama está a caminho de outra tela, e um `await` que rejeita
   * viraria um erro não tratado no meio de um clique que funcionou. O 404 do
   * upstream (recomendação de outro tenant) também é engolido AQUI, e não no
   * BFF — lá ele atravessa de propósito, para que este caminho seja o único
   * lugar que decide ignorá-lo.
   */
  marcarComoVista(id: string): void {
    void api
      .post(`${BASE}/recommendations/${id}/viewed`)
      .catch(() => undefined);
  },

  /**
   * GET /api/v1/radar/companies/{cnpj}/news — recent news about a company (F3).
   *
   * Lazy by design: the UI only calls this when the user expands the
   * "Notícias" panel (cost control — we do NOT fetch news for every card).
   * Pass `refresh=true` to bypass the server-side cache.
   */
  async getCompanyNews(cnpj: string, refresh = false): Promise<CompanyNews> {
    const digits = (cnpj || '').replace(/\D/g, '');
    const { data } = await api.get<CompanyNews>(
      `${BASE}/companies/${digits}/news`,
      refresh ? { params: { refresh: true } } : undefined,
    );
    return data;
  },
};
